import { useEffect, useMemo, useRef, useState } from 'react';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { api } from '@/api/client';
import { orphanSetOf } from './useImports';
import type { ImportEvent, ImportOrphanSet, ImportStepPayload } from './useImports';

/**
 * The live log of one import.
 *
 * Connects to `/hubs/imports` with `skipNegotiation` over WebSockets. That is not an
 * optimisation: the application runs as several load-balanced instances with no sticky
 * sessions, so a negotiate request and the socket that follows it could land on different
 * instances and the connection would never establish. Going straight to the WebSocket makes it
 * a single request, which the ALB routes once.
 *
 * On connect — and again after every automatic reconnect — the hook calls `JoinJobAsync`,
 * which joins the job's group and returns the events logged so far. Those are merged into the
 * same list as the live ones, keyed by `seq`, so a reload mid-run, a late join, or a gap
 * caused by a dropped socket all end with the same complete log and no duplicates.
 *
 * `seq` is assigned by Postgres and is what makes this work: the client never has to reason
 * about ordering or about which instance produced an event.
 */

export type ImportStreamStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface ImportStream {
  events: ImportEvent[];
  /** The most recent `step` event, for the progress bar. */
  step: ImportStepPayload | null;
  /** The `summary` payload once the run has reported one. */
  summary: Record<string, unknown> | null;
  /** The orphan-rows downloads the run has offered so far, in the order it offered them. */
  orphanSets: ImportOrphanSet[];
  status: ImportStreamStatus;
}

/** Inserts (or replaces) an event, keeping the list ordered by `seq`. */
function merge(current: ImportEvent[], incoming: ImportEvent[]): ImportEvent[] {
  if (incoming.length === 0) return current;

  const bySeq = new Map(current.map((event) => [event.seq, event]));
  for (const event of incoming) bySeq.set(event.seq, event);

  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export function useImportStream(jobId: string | null): ImportStream {
  const [events, setEvents] = useState<ImportEvent[]>([]);
  const [status, setStatus] = useState<ImportStreamStatus>('idle');

  // The highest sequence number seen, so a rejoin asks only for what it missed. A ref rather
  // than state because the connection callbacks must read the current value without being
  // rebuilt every time an event arrives.
  const lastSeq = useRef(0);

  useEffect(() => {
    setEvents([]);
    lastSeq.current = 0;

    if (!jobId) {
      setStatus('idle');
      return;
    }

    let disposed = false;
    setStatus('connecting');

    // The antiforgery cookie is irrelevant here (the handshake is a GET), but the
    // authentication cookie is not: it rides the WebSocket handshake like any other request,
    // which is why the hub can be [Authorize]d with no token plumbing on the client.
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl('/hubs/imports', {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const receive = (event: ImportEvent) => {
      if (disposed) return;
      lastSeq.current = Math.max(lastSeq.current, event.seq);
      setEvents((current) => merge(current, [event]));
    };

    // Joining is done on first connect and on every reconnect, always asking only for what
    // came after the last event we hold.
    const join = async () => {
      const missed = await connection.invoke<ImportEvent[]>('JoinJobAsync', jobId, lastSeq.current);
      if (disposed || missed.length === 0) return;
      lastSeq.current = Math.max(lastSeq.current, ...missed.map((event) => event.seq));
      setEvents((current) => merge(current, missed));
    };

    connection.on('event', receive);
    connection.onreconnecting(() => !disposed && setStatus('reconnecting'));
    connection.onreconnected(async () => {
      if (disposed) return;
      setStatus('connected');
      try {
        await join();
      } catch {
        // The REST fallback below still closes the gap on the next render.
      }
    });

    connection
      .start()
      .then(async () => {
        if (disposed) return;
        setStatus('connected');
        await join();
      })
      .catch(async () => {
        if (disposed) return;
        // No socket — a proxy that will not upgrade, say. The log is still readable over
        // REST, so the page degrades to a static view of what has happened so far rather
        // than showing nothing at all.
        setStatus('failed');
        try {
          const fallback = await api.get<ImportEvent[]>(`/api/admin/imports/${jobId}/events`);
          if (!disposed) setEvents((current) => merge(current, fallback));
        } catch {
          // Nothing more to try; the page shows the connection as failed.
        }
      });

    return () => {
      disposed = true;
      connection.off('event', receive);
      // Leaving the group explicitly is pointless once the connection is gone, and stop()
      // during the handshake rejects; neither is worth reporting.
      void connection.stop().catch(() => undefined);
    };
  }, [jobId]);

  // Derived from the same list rather than tracked separately: replay and live events then
  // cannot disagree, and a late joiner sees the last step and the summary immediately.
  const { step, summary, orphanSets } = useMemo(() => {
    let lastStep: ImportStepPayload | null = null;
    let lastSummary: Record<string, unknown> | null = null;
    const sets: ImportOrphanSet[] = [];

    for (const event of events) {
      if (event.kind === 'step') lastStep = event.payload as ImportStepPayload;
      else if (event.kind === 'summary') lastSummary = event.payload as Record<string, unknown>;
      else if (event.kind === 'orphans') {
        const set = orphanSetOf(event);
        if (!sets.includes(set)) sets.push(set);
      }
    }

    return { step: lastStep, summary: lastSummary, orphanSets: sets };
  }, [events]);

  return { events, step, summary, orphanSets, status };
}
